import { useId, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
// Types `theme.vars`, which the theme has because it is built with CSS
// variables (`theme.ts`).
import type {} from '@mui/material/themeCssVarsAugmentation'

/** One bar: a value, or none for a gap, and what it says when it is pointed at. */
export interface Bar {
  key: string
  /** Absent for a period nothing was recorded for, which is drawn as a gap, not a zero. */
  value?: number
  /** A period not over yet, drawn fainter. */
  partial?: boolean
  /**
   * A value summed from only some of the period: drawn hatched, so a bar
   * short of days does not pass for a quiet one. `detail` says how short.
   */
  incomplete?: boolean
  /** The sentence the bar answers with on hover, focus or tap. */
  detail: string
}

/** The same figures as rows, for anyone who cannot use the bars. */
export interface BarTable {
  head: string[]
  rows: { key: string; cells: string[] }[]
}

const WIDTH = 260
const HEIGHT = 84
const BASELINE = HEIGHT - 1
const TOP = 6
const BAR = 10
const RADIUS = 3

/**
 * A row of thin columns over one baseline, drawn as SVG.
 *
 * **Hand-drawn, not charted.** The admin carries `@mui/x-charts` for
 * the daily sparkline beside these (docs/plan/site-traffic.md §8), and
 * these stay its own because a library chart would carry none of what
 * makes them usable without a mouse: the table view, a focus stop per
 * week and the line that announces it. It follows the rules the plan
 * sets for it: thin bars with rounded ends and
 * square feet, one series colour (a bar short of days is hatched in it
 * and outlined, not given a second colour), text in the theme's text colours and
 * never in the series colour, no gridlines beyond the baseline, and a
 * table for anyone who cannot use the bars. Every colour is the theme's,
 * read as its CSS variable, so it is right in light and dark without
 * being drawn twice.
 *
 * **Every bar answers focus and tap as well as hover**, in a line under
 * the bars rather than a floating tooltip: a tooltip is a hover idea, and
 * a line that is always there is the same for a mouse, a finger and a
 * keyboard, and is announced as it changes. The SVG itself is one image
 * named by `label`, so a screen reader hears the headline once rather
 * than a dozen unnamed shapes; the table is where it reads the figures.
 * A bar is a focus stop and nothing more to assistive technology — inside
 * an image its own name would not be read — so what a bar says on focus
 * reaches a screen reader through the line, which is a live region.
 *
 * **Hover, focus and a tap are three states, not one.** A tap on a phone
 * also fires an emulated pointer-enter and a focus, and if either set the
 * same state as hover nothing would ever clear it: the tap could not be
 * let go. So hover counts only a mouse, focus counts only a focus that a
 * pointer did not cause (the keyboard's, which also draws the ring), and
 * a tap stays until the same bar is tapped again. Shown, in that order:
 * what the mouse is over, what the keyboard is on, what was tapped.
 */
export function WeeklyBars({
  label,
  bars,
  table,
  first,
  last,
}: {
  /** The image's accessible name: the headline, in words. */
  label: string
  bars: Bar[]
  table: BarTable
  /** What the axis says under the first bar and the last. */
  first: string
  last: string
}) {
  const theme = useTheme()
  const palette = (theme.vars ?? theme).palette
  const [hovered, setHovered] = useState<number | null>(null)
  const [focused, setFocused] = useState<number | null>(null)
  const [tapped, setTapped] = useState<number | null>(null)
  const [asTable, setAsTable] = useState(false)
  // Whether a pointer is down, so the focus it brings with it is not
  // taken for the keyboard's.
  const pointing = useRef(false)
  const shown = hovered ?? focused ?? tapped
  // Unique per card, so two cards on a screen never share a pattern. The
  // colons React puts in an id would end a `url(#…)` reference early.
  const hatch = `hatch-${useId().replace(/:/g, '')}`

  const most = Math.max(0, ...bars.map((bar) => bar.value ?? 0))
  const slot = WIDTH / bars.length

  return (
    <Stack spacing={1}>
      {asTable ? (
        <Table size="small" aria-label={label}>
          <TableHead>
            <TableRow>
              {table.head.map((heading, index) => (
                <TableCell key={heading} align={index === 0 ? 'left' : 'right'}>
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row) => (
              <TableRow key={row.key}>
                {row.cells.map((cell, index) => (
                  <TableCell
                    key={index}
                    align={index === 0 ? 'left' : 'right'}
                    sx={index === 0 ? undefined : { fontVariantNumeric: 'tabular-nums' }}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <>
          <Box
            component="svg"
            role="img"
            aria-label={label}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            sx={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible' }}
          >
            <defs>
              <pattern id={hatch} width={3} height={3} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width={1.5} height={3} fill={palette.primary.main} />
              </pattern>
            </defs>
            {bars.map((bar, index) => {
              const x = index * slot
              const height =
                bar.value === undefined ? 0 : Math.max(1.5, most === 0 ? 0 : ((BASELINE - TOP) * bar.value) / most)
              const active = shown === index
              const ring = focused === index
              return (
                <g
                  key={bar.key}
                  data-bar={bar.key}
                  tabIndex={0}
                  onPointerEnter={(event) => event.pointerType === 'mouse' && setHovered(index)}
                  onPointerLeave={(event) => event.pointerType === 'mouse' && setHovered(null)}
                  onPointerDown={() => {
                    pointing.current = true
                    // Not on pointer-up: a tap's emulated mouse-down, which
                    // is what focuses the bar, comes after it. The click
                    // that ends the gesture, a cancelled one, or any key
                    // (the keyboard's own focus follows a key) ends it.
                    const release = () => {
                      pointing.current = false
                      for (const type of ['click', 'pointercancel', 'keydown']) window.removeEventListener(type, release, true)
                    }
                    for (const type of ['click', 'pointercancel', 'keydown']) window.addEventListener(type, release, true)
                  }}
                  onFocus={() => !pointing.current && setFocused(index)}
                  onBlur={() => setFocused(null)}
                  onClick={() => setTapped(tapped === index ? null : index)}
                  // The ring is drawn below, on the slot, where the
                  // browser's own outline round a group would be lost.
                  style={{ cursor: 'pointer', outline: 'none' }}
                >
                  {/* The hit target is the whole slot, not the painted
                      pixels: a short bar is still easy to land on. */}
                  <rect
                    x={x + 0.75}
                    y={0.75}
                    width={slot - 1.5}
                    height={BASELINE - 1.5}
                    rx={2}
                    fill={active ? palette.action.selected : 'transparent'}
                    stroke={ring ? palette.primary.main : 'none'}
                    strokeWidth={1.5}
                    data-focus-ring={ring || undefined}
                  />
                  {bar.value !== undefined && (
                    <path
                      d={column(x + (slot - BAR) / 2, BASELINE - height, BAR, height)}
                      fill={bar.incomplete ? `url(#${hatch})` : palette.primary.main}
                      fillOpacity={bar.partial ? 0.45 : 1}
                      stroke={bar.incomplete ? palette.primary.main : 'none'}
                      strokeOpacity={bar.partial ? 0.45 : 1}
                      strokeWidth={1}
                      data-incomplete={bar.incomplete || undefined}
                    />
                  )}
                </g>
              )
            })}
            <line x1={0} x2={WIDTH} y1={BASELINE + 0.5} y2={BASELINE + 0.5} stroke={palette.divider} strokeWidth={1} />
          </Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {first}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {last}
            </Typography>
          </Stack>
          {/* Always the same height, so pointing at a bar never moves the
              card under the pointer. */}
          <Typography variant="body2" aria-live="polite" sx={{ minHeight: '1.55em' }} color={shown === null ? 'text.secondary' : 'text.primary'}>
            {shown === null ? 'Point at or tap a week to see its numbers.' : bars[shown].detail}
          </Typography>
        </>
      )}
      <Box>
        <Button size="small" onClick={() => setAsTable(!asTable)} sx={{ ml: -1 }}>
          {asTable ? 'Show as bars' : 'Show as a table'}
        </Button>
      </Box>
    </Stack>
  )
}

/** A column with rounded top corners and a square foot on the baseline. */
function column(x: number, y: number, width: number, height: number): string {
  const r = Math.min(RADIUS, width / 2, height)
  return [
    `M${x},${y + height}`,
    `V${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `H${x + width - r}`,
    `Q${x + width},${y} ${x + width},${y + r}`,
    `V${y + height}`,
    'Z',
  ].join(' ')
}
